import type {Stripe} from '@stripe/stripe-js';
import {loadStripe} from '@stripe/stripe-js/pure';

import {isDev, useStripeTestEnv} from 'app/utils/envUtils';

/**
 * Which Stripe account a client secret belongs to. The backend decides per organization from
 * `Organization.stripe_test` and returns it alongside every secret, because a test-mode secret
 * mounted against the live publishable key fails inside Elements with an opaque error.
 */
export type StripeMode = 'test' | 'live';

const PUBLISHABLE_KEYS: Record<StripeMode, string> = {
  test: 'pk_test_FyQLZnD7ra4YjsEr2hYImdKN',
  live: 'pk_live_UXVhffZesv3DY2GEgqOGQSJK',
};

/**
 * The smallest card charge Stripe accepts in USD. Elements also enforces it when the payment
 * method types are pinned: a deferred-intent session created with a smaller amount fails to load.
 * https://docs.stripe.com/currencies#minimum-and-maximum-charge-amounts
 */
export const STRIPE_MINIMUM_CHARGE_CENTS = 50;

/** The mode this build talks to when nothing per-organization says otherwise. */
export const DEFAULT_STRIPE_MODE: StripeMode = useStripeTestEnv ? 'test' : 'live';

export const STRIPE_API_KEY = PUBLISHABLE_KEYS[DEFAULT_STRIPE_MODE];

// Stripe draws its testing assistant over any page with Elements on a test key. Internal and demo
// orgs use test keys in production, so it stays limited to local dev.
// https://docs.stripe.com/sdks/stripejs-testing-assistant
export const STRIPE_OPTIONS = {developerTools: {assistant: {enabled: isDev}}};

// Import from `@stripe/stripe-js/pure` (not the default entry) — the default entry injects
// `<script src="https://js.stripe.com/v3">` as a side effect on import, which would fetch Stripe on
// every page that pulls this module in. `/pure` defers script injection until `loadStripe()` is
// actually called, so the promises below are created lazily and memoized per mode.
const stripePromises: Partial<Record<StripeMode, Promise<Stripe | null>>> = {};

export function getStripePromise(mode: StripeMode): Promise<Stripe | null> {
  let promise = stripePromises[mode];
  if (!promise) {
    promise = loadStripe(PUBLISHABLE_KEYS[mode], STRIPE_OPTIONS);
    stripePromises[mode] = promise;
  }
  return promise;
}
